/**
 * Media Gallery Custom Component
 * Implements thumbnail click interaction for the product media gallery
 * 
 * Requirements: 2.4
 * - Click handlers to update main image source
 * - Apply blue border (#7EADE7) to active thumbnail
 * - Remove border from previously active thumbnail
 */

(() => {
  // Allow the script to be included multiple times (common in Shopify themes)
  // without throwing "Identifier has already been declared".
  if (typeof window !== 'undefined' && window.MediaGalleryCustom) return;

class MediaGalleryCustom {
  constructor(container) {
    this.container = container;
    this.thumbnails = container.querySelectorAll('.media-gallery-custom__thumbnail');
    this.mediaItems = container.querySelectorAll('.media-gallery-custom__media-item');
    this.currentIndex = 0;

    // Some templates use a simple thumbs container (no [data-thumb-slider]).
    // Always track horizontal thumbnail rows so low-count centering can work everywhere.
    this.thumbnailTracksHorizontal = Array.from(
      container.querySelectorAll('.media-gallery-custom__thumbnails--horizontal')
    );

    this.thumbnailSliders = Array.from(
      container.querySelectorAll('[data-thumb-slider]')
    ).map((sliderEl) => {
      const track = sliderEl.querySelector(
        '.media-gallery-custom__thumbnails--horizontal'
      );
      return {
        sliderEl,
        track,
        prevBtn: sliderEl.querySelector('[data-thumb-slider-prev]'),
        nextBtn: sliderEl.querySelector('[data-thumb-slider-next]')
      };
    });
    
    this.mainImageContainers = container.querySelectorAll('[data-main-image-container], [data-main-image-container-mobile]');
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.bindSliderEvents();
    this.updateLowCountClasses();
    this.updateSliderButtons();
    this.initZoom();
    this.initModalSync();
  }

  initZoom() {
    this.mainImageContainers.forEach(container => {
      container.addEventListener('mousemove', (e) => {
        const activeItem = container.querySelector('.media-gallery-custom__media-item--active');
        if (!activeItem) return;
        const img = activeItem.querySelector('img[data-zoom-image]');
        if (!img) return;

        const rect = container.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        
        img.style.transformOrigin = `${x}% ${y}%`;
      });

      container.addEventListener('mouseleave', () => {
        const activeItem = container.querySelector('.media-gallery-custom__media-item--active');
        if (!activeItem) return;
        const img = activeItem.querySelector('img[data-zoom-image]');
        if (img) img.style.transformOrigin = 'center';
      });

      // Make the whole image area clickable to open modal
      container.addEventListener('click', (e) => {
        const activeItem = container.querySelector('.media-gallery-custom__media-item--active');
        if (activeItem && (activeItem.querySelector('video') || activeItem.querySelector('iframe'))) return;
        
        const zoomBtn = container.querySelector('.media-gallery-custom__zoom-button');
        if (zoomBtn && e.target !== zoomBtn && !zoomBtn.contains(e.target)) {
          zoomBtn.click();
        }
      });
    });
  }

  initModalSync() {
    const modal = document.querySelector(`.product-media-modal`);
    if (!modal) return;

    const openers = this.container.querySelectorAll('.product-modal-opener');
    openers.forEach(opener => {
      opener.addEventListener('click', () => {
        const activeMedia = this.container.querySelector('.media-gallery-custom__media-item--active');
        if (activeMedia) {
          const mediaId = activeMedia.dataset.mediaId;
          setTimeout(() => {
            const modalItem = modal.querySelector(`[data-media-id="${mediaId}"]`);
            if (modalItem) {
              modalItem.scrollIntoView();
            }
          }, 50);
        }
      });
    });
  }

  updateLowCountClasses() {
    this.thumbnailTracksHorizontal.forEach((track) => {
      if (!track) return;

      const cs = window.getComputedStyle(track);
      const isHorizontal = cs.flexDirection !== 'column';
      if (!isHorizontal) {
        track.classList.remove('media-gallery-custom__thumbnails--low-count');
        return;
      }

      // Some breakpoints/templates use only the modifier class
      // (.media-gallery-custom__thumbnail--horizontal) without the base class.
      // Count unique elements across both selectors.
      const thumbEls = track.querySelectorAll(
        '.media-gallery-custom__thumbnail, .media-gallery-custom__thumbnail--horizontal'
      );
      const count = new Set(Array.from(thumbEls)).size;
      if (count > 0 && count <= 2) {
        track.classList.add('media-gallery-custom__thumbnails--low-count');
      } else {
        track.classList.remove('media-gallery-custom__thumbnails--low-count');
      }
    });
  }

  bindEvents() {
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => this.handleThumbnailClick(index));
    });
  }

  bindSliderEvents() {
    this.thumbnailSliders.forEach(({ track, prevBtn, nextBtn }) => {
      if (!track || !prevBtn || !nextBtn) return;

      prevBtn.addEventListener('click', () => {
        this.previous();
      });
      nextBtn.addEventListener('click', () => {
        this.next();
      });

      // Keep buttons in sync while the user scrolls/resize.
      let rafId = null;
      const scheduleUpdate = () => {
        if (rafId) return;
        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          this.updateSliderButtons();
        });
      };

      track.addEventListener('scroll', scheduleUpdate, { passive: true });
      window.addEventListener('resize', scheduleUpdate, { passive: true });
    });
  }

  updateSliderButtons() {
    this.thumbnailSliders.forEach(({ prevBtn, nextBtn }) => {
      if (!prevBtn || !nextBtn) return;

      const totalItems = this.thumbnails.length / (this.thumbnailTracksHorizontal.length || 1);
      
      prevBtn.disabled = this.currentIndex <= 0;
      nextBtn.disabled = this.currentIndex >= totalItems - 1;
    });
  }

  /**
   * Handle thumbnail click event
   * Updates main image and active thumbnail state
   * @param {number} index - Index of clicked thumbnail
   */
  handleThumbnailClick(index) {
    if (index === this.currentIndex) return;

    // Update active thumbnail
    this.updateActiveThumbnail(index);
    
    // Update visible media item
    this.updateActiveMedia(index);
    
    // Update current index
    this.currentIndex = index;

    // Ensure active thumbnail is visible in all sliders.
    this.scrollActiveThumbnailIntoView(index);
    this.updateSliderButtons();
  }

  scrollActiveThumbnailIntoView(index) {
    const targets = this.container.querySelectorAll(
      `.media-gallery-custom__thumbnails--horizontal [data-thumbnail-index="${index}"]`
    );
    targets.forEach((el) => {
      if (typeof el.scrollIntoView !== 'function') return;

      const track = el.closest('.media-gallery-custom__thumbnails--horizontal');
      if (!track) return;
      
      const cs = window.getComputedStyle(track);
      const vertical = cs.flexDirection === 'column' || (track.scrollHeight > track.clientHeight + 1);

      el.scrollIntoView({
        behavior: 'smooth',
        block: vertical ? 'center' : 'nearest',
        inline: vertical ? 'nearest' : 'center'
      });
    });
  }

  /**
   * Update active thumbnail styling
   * Removes active class from previous, adds to new
   * @param {number} newIndex - Index of new active thumbnail
   */
  updateActiveThumbnail(newIndex) {
    // Remove active class from all thumbnails
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.classList.remove('media-gallery-custom__thumbnail--active');
    });

    // Add active class to clicked thumbnail
    // Note: We need to update both desktop and mobile thumbnails
    const allThumbnails = this.container.querySelectorAll(
      `[data-thumbnail-index="${newIndex}"]`
    );
    allThumbnails.forEach((thumbnail) => {
      thumbnail.classList.add('media-gallery-custom__thumbnail--active');
    });
  }

  /**
   * Update visible media item
   * Hides previous, shows new
   * @param {number} newIndex - Index of new active media
   */
  updateActiveMedia(newIndex) {
    // Hide all media items and remove active class
    this.mediaItems.forEach((item) => {
      item.hidden = true;
      item.classList.remove('media-gallery-custom__media-item--active');
    });

    // Show media items at new index (both desktop and mobile containers)
    const allMediaItems = this.container.querySelectorAll(
      `[data-media-index="${newIndex}"]`
    );
    allMediaItems.forEach((item) => {
      item.hidden = false;
      item.classList.add('media-gallery-custom__media-item--active');
    });
  }

  /**
   * Navigate to specific media by index
   * @param {number} index - Target media index
   */
  goToMedia(index) {
    const totalItems = this.thumbnails.length / (this.thumbnailTracksHorizontal.length || 1);
    if (index >= 0 && index < totalItems) {
      this.handleThumbnailClick(index);
    }
  }

  /**
   * Navigate to next media
   */
  next() {
    const totalItems = this.thumbnails.length / (this.thumbnailTracksHorizontal.length || 1);
    if (this.currentIndex < totalItems - 1) {
      this.handleThumbnailClick(this.currentIndex + 1);
    }
  }

  /**
   * Navigate to previous media
   */
  previous() {
    if (this.currentIndex > 0) {
      this.handleThumbnailClick(this.currentIndex - 1);
    }
  }
}

// Initialize all media galleries on the page
function initMediaGalleries() {
  const galleries = document.querySelectorAll('[data-media-gallery]');
  galleries.forEach((gallery) => {
    // Prevent multiple initializations on the same element
    if (gallery.dataset.initialized) return;
    new MediaGalleryCustom(gallery);
    gallery.dataset.initialized = 'true';
  });
}

document.addEventListener('DOMContentLoaded', initMediaGalleries);

// Listen for shopify section loads or custom product swaps
document.addEventListener('shopify:section:load', initMediaGalleries);
document.addEventListener('product-info:loaded', initMediaGalleries);

if (typeof window !== 'undefined') {
  window.MediaGalleryCustom = MediaGalleryCustom;
  window.initMediaGalleries = initMediaGalleries;
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { MediaGalleryCustom: (typeof window !== 'undefined' ? window.MediaGalleryCustom : MediaGalleryCustom) };
}

})();
